import { Chip, IconButton, Box, Tooltip } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridColDef,
} from '@mui/x-data-grid-pro';
import moment from 'moment';

import { IcDotSpread, IcLock, IcUnlock, IcWarning } from '@/assets/images';
import { customNumericOperators } from '@/components';
import { excludeValues, formatDate } from '@/components/DataGrid/utils';
import { IAddress, IIncidentCode, IUser } from '@/models';
import { KaseStatusList } from '@/pages/Kases/data';
import { useUserStore } from '@/store';
import { formatPersonName, userHasCaseAccess } from '@/utils';

import { RestrictedTableCell } from '../styles';

const getCurrentUser = () => {
  const { user } = useUserStore();
  return user;
};

const renderPriorityCell = (isPriority: boolean) =>
  isPriority ? <IcWarning color="red" style={{ height: '100%' }} /> : null;

const getIsPriority = (row: any, value?: any) =>
  Boolean(
    row?.priorityInfo?.isPriority ?? row?.isPriority ?? row?.priority ?? value,
  );

const sortDates = (a?: any, b?: any) => {
  const dateA = a?.length ? moment(a[a.length - 1].approvedAt).toDate() : null;
  const dateB = b?.length ? moment(b[b.length - 1].approvedAt).toDate() : null;

  if (!dateA && !dateB) return 0;
  if (!dateA) return 1;
  if (!dateB) return -1;
  return dateA.getTime() - dateB.getTime();
};

const priority = (): Partial<GridColDef> => ({
  filterable: false,
  renderCell: (params) => {
    const user = getCurrentUser();
    const userWithCaseAccess = user
      ? userHasCaseAccess(user, params.row)
      : false;
    const isCaseRestricted = params.row.caseRestrictedBy != null;
    const isPriority = getIsPriority(params.row, params.value);

    return !isCaseRestricted || userWithCaseAccess ? (
      renderPriorityCell(isPriority)
    ) : (
      <RestrictedTableCell />
    );
  },
  sortComparator: (a, b) =>
    (getIsPriority(b) ? 1 : 0) - (getIsPriority(a) ? 1 : 0),
});

const address = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    return params.row?.address?.normalized;
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues([
      'isAnyOf',
      'isNotEmpty',
      'isEmpty',
      'equals',
      'doesNotEqual',
    ]),
  ),
  valueGetter: (a: IAddress) => a?.normalized,
  sortComparator: (a, b) =>
    (a?.toLowerCase() || '').localeCompare(b?.toLowerCase() || ''),
});

const createdBy = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const { firstName, lastName } = params.row.createdBy as IUser;
    return formatPersonName({ firstName, lastName });
  },
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isNotEmpty', 'isEmpty']),
  ),
});

const approvedInfo = (): Partial<GridColDef> => ({
  renderCell: (params) => {
    const user = getCurrentUser();
    const userWithCaseAccess = user
      ? userHasCaseAccess(user, params.row)
      : false;
    const isCaseRestricted = params.row.caseRestrictedBy != null;

    const kaseStatus =
      KaseStatusList[params.row.status as keyof typeof KaseStatusList];

    return !isCaseRestricted || userWithCaseAccess ? (
      kaseStatus === KaseStatusList.APPROVED_BY_SUPERVISOR ||
      kaseStatus === KaseStatusList.ARCHIVED ? (
        formatDate(params.row.approvedInfo?.slice(-1)[0]?.approvedAt || '')
      ) : (
        ''
      )
    ) : (
      <RestrictedTableCell />
    );
  },
  sortComparator: sortDates,
  filterOperators: getGridDateOperators(true),
});

const number = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isNotEmpty', 'isEmpty', 'isAnyOf']),
  ),
});

const getDateColumn = (key: string): Partial<GridColDef> => ({
  filterOperators: getGridDateOperators(true).filter(
    excludeValues(['isNotEmpty', 'isEmpty']),
  ),
  renderCell: (params) => {
    if (key.toLowerCase() === 'updatedat') {
      const user = getCurrentUser();
      const userWithCaseAccess = user
        ? userHasCaseAccess(user, params.row)
        : false;
      const isCaseRestricted = params.row.caseRestrictedBy != null;
      return !isCaseRestricted || userWithCaseAccess ? (
        formatDate(params.row[key])
      ) : (
        <RestrictedTableCell />
      );
    } else {
      return formatDate(params.row[key]);
    }
  },
});

const formatIncidentCodes = (incidentCodes: IIncidentCode[]): string => {
  return (incidentCodes ?? [])
    .map((ic) => (ic.description ? `${ic.code} - ${ic.description}` : ic.code))
    .join(', ');
};

const getIncidentCodesColumn = (): Partial<GridColDef> => ({
  filterOperators: getGridStringOperators().filter(
    excludeValues(['isAnyOf', 'isEmpty', 'isNotEmpty']),
  ),
  renderCell: (params) => {
    const value = params.row?.incidentCodes as IIncidentCode[] | undefined;
    return formatIncidentCodes(value || []);
  },
});

const masterNameCount = (): Partial<GridColDef> => ({
  filterOperators: customNumericOperators.filter(
    excludeValues(['isAnyOf', 'isEmpty', 'isNotEmpty']),
  ),
  renderCell: (params) => {
    const user = getCurrentUser();
    const userWithCaseAccess = user
      ? userHasCaseAccess(user, params.row)
      : false;
    const isCaseRestricted = params.row.caseRestrictedBy != null;

    return !isCaseRestricted || userWithCaseAccess ? (
      params.row?.masterNameCount ?? 0
    ) : (
      <RestrictedTableCell />
    );
  },
});

const status = (): Partial<GridColDef> => ({
  filterable: false,
  renderCell: (params) => {
    const user = getCurrentUser();
    const userWithCaseAccess = user
      ? userHasCaseAccess(user, params.row)
      : false;

    const isCaseRestricted = params.row && params.row.caseRestrictedBy != null;

    if (!isCaseRestricted || userWithCaseAccess) {
      const kaseStatus =
        KaseStatusList[params.value as keyof typeof KaseStatusList];
      return (
        <Chip
          label={kaseStatus.label}
          sx={{
            backgroundColor: kaseStatus.bgColor,
            color: kaseStatus.color,
            fontWeight: 500,
            fontSize: '12px',
            height: '25px',
            borderRadius: '6px',
          }}
        />
      );
    } else {
      return <RestrictedTableCell />;
    }
  },
});

const actions = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, id: string) => void,
): Partial<GridColDef> => ({
  filterable: false,
  sortable: false,
  renderCell: (params) => {
    const user = getCurrentUser();
    const userWithCaseAccess = user
      ? userHasCaseAccess(user, params.row)
      : false;
    const caseRestrictedBy = params.row.caseRestrictedBy
      ? formatPersonName(params.row.caseRestrictedBy)
      : undefined;

    const isCaseRestricted = params.row && params.row.caseRestrictedBy != null;
    const usersWithAccess = (params.row.usersWithCaseAccess || [])
      .map((u_: IUser) => formatPersonName(u_))
      .join('\n');
    const tooltipTitle =
      !userWithCaseAccess && caseRestrictedBy
        ? `Restricted by ${caseRestrictedBy}`
        : `Restricted by ${caseRestrictedBy} \nAllowed users:\n${usersWithAccess}`;

    const RestrictionIcon = isCaseRestricted
      ? userWithCaseAccess
        ? IcUnlock
        : IcLock
      : null;

    return (
      <Box
        height="100%"
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{
          background: 'transparent',
          marginLeft: !RestrictionIcon ? '18px' : '', // For UI align
        }}
      >
        {RestrictionIcon && (
          <Tooltip
            key={tooltipTitle}
            title={
              <span style={{ whiteSpace: 'pre-line' }}>{tooltipTitle}</span>
            }
          >
            <Box sx={{ display: 'flex' }}>
              <RestrictionIcon
                style={{ width: '13px', height: '20px', marginRight: '5px' }}
              />
            </Box>
          </Tooltip>
        )}
        <IconButton onClick={(e) => openMenu?.(e, params.row._id)}>
          <IcDotSpread />
        </IconButton>
      </Box>
    );
  },
});

export const kaseCols = (
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void,
) => {
  return {
    number,
    priority,
    address,
    createdBy,
    incidentCodes: () => getIncidentCodesColumn(),
    approvedInfo,
    createdAt: () => getDateColumn('createdAt'),
    updatedAt: () => getDateColumn('updatedAt'),
    status,
    actions: () => actions(openMenu),
    masterNameCount,
  };
};
